import { notifyApi } from '@/api/su/notify';
import type { InotificationQueueListStateObj } from '@/api/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type { INotificationsRaw, IThunkAPI, notificationQueueObjRaw } from '@/store/types';

const notificationListObjInitialvalue = {
  transformedList: [],
  sendVoiceCount: 0,
  sendSmsCount: 0,
  sendEmailCount: 0,
  unDeliveredVoiceCount: 0,
  unDeliveredSmsCount: 0,
  unDeliveredEmailCount: 0,
};

const initialState: InotificationQueueListStateObj = {
  notificationQueueListObj: notificationListObjInitialvalue,
  isNotificationQueueError: false,
  isNotificationQueueSuccess: false,
  isNotificationQueueFetching: false,
  selectedNotification: {
    jobId: 0,
    notification: null,
  },
};

export const getNotifyQueueListByNotifyJobId = createAsyncThunk<notificationQueueObjRaw[], number, IThunkAPI>(
  'get/notification/su/scheduleList',
  async (notifyJobId, thunkAPI) => {
    try {
      const response = await notifyApi.getNotifyQueueList(notifyJobId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

const transformNotificationQueueLists = (notificationQueueList: notificationQueueObjRaw[]) => {
  let transformedList: notificationQueueObjRaw[] = [];
  let sendVoiceCount = 0;
  let sendSmsCount = 0;
  let sendEmailCount = 0;
  let unDeliveredVoiceCount = 0;
  let unDeliveredSmsCount = 0;
  let unDeliveredEmailCount = 0;
  if (notificationQueueList && Array.isArray(notificationQueueList)) {
    transformedList = notificationQueueList?.map(notification => {
      let email = ' ';
      let sms = ' ';
      let lxEmail = ' ';
      let phone = ' ';
      if (notification.email === '800') {
        email = 'Sent';
        sendEmailCount++;
      }
      if (notification.lxEmail === '800') {
        lxEmail = 'Sent';
        sendEmailCount++;
      }
      if (notification.lxEmail !== '800' || notification.email !== '800') {
        unDeliveredEmailCount++;
      }

      // sms statuses
      if (notification.sms === '350') {
        sms = 'Queued';
        unDeliveredSmsCount++;
      }
      if (notification.sms === '351') {
        sms = 'Sent';
        sendSmsCount++;
      }
      if (notification.sms === '352') {
        sms = 'Failed';
        unDeliveredSmsCount++;
      }
      if (notification.sms === '353') {
        sms = 'Delivered';
        sendSmsCount++;
      }
      if (notification.sms === '354') {
        sms = 'Undelivered';
        unDeliveredSmsCount++;
      }
      if (notification.sms === '355') {
        sms = 'Rejected';
        unDeliveredSmsCount++;
      }

      // phone statuses
      if (notification.phone === '340') {
        phone = 'Completed';
        sendVoiceCount++;
      }
      if (notification.phone === '341') {
        phone = 'Busy';
        unDeliveredVoiceCount++;
      }
      if (notification.phone === '342') {
        phone = 'Failed';
        unDeliveredVoiceCount++;
      }
      if (notification.phone === '343') {
        phone = 'Timeout';
        unDeliveredVoiceCount++;
      }
      if (notification.phone === '344') {
        phone = 'NoAnswer';
        unDeliveredVoiceCount++;
      }
      if (notification.phone === '345') {
        phone = 'InProgress';
        unDeliveredVoiceCount++;
      }

      return {
        ...notification,
        email: email,
        sms: sms,
        lxEmail: lxEmail,
        phone: phone,
      };
    });
  }
  return {
    transformedList,
    sendVoiceCount,
    sendSmsCount,
    sendEmailCount,
    unDeliveredVoiceCount,
    unDeliveredSmsCount,
    unDeliveredEmailCount,
  };
};

export const getNotificationStatusSlice = createSlice({
  name: 'createNotification',
  initialState,
  reducers: {
    updateSelectedNotifyJobId(state, { payload }) {
      state.selectedNotification = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getNotifyQueueListByNotifyJobId.fulfilled, (state, action) => {
        const notificationQueueListObj = transformNotificationQueueLists(action?.payload || []);
        state.notificationQueueListObj = notificationQueueListObj;
        state.isNotificationQueueSuccess = true;
        state.isNotificationQueueError = false;
        state.isNotificationQueueFetching = false;
      })
      .addCase(getNotifyQueueListByNotifyJobId.rejected, state => {
        state.isNotificationQueueError = true;
        state.isNotificationQueueSuccess = false;
        state.isNotificationQueueFetching = false;
      })
      .addCase(getNotifyQueueListByNotifyJobId.pending, state => {
        state.isNotificationQueueFetching = true;
      });
  },
});

// actions
export const { updateSelectedNotifyJobId } = getNotificationStatusSlice.actions;

// selectors

export const getNotificationQueueLists = (
  state: RootState,
): InotificationQueueListStateObj['notificationQueueListObj'] => {
  return state.notificationQueue?.notificationQueueListObj?.transformedList?.length
    ? state.notificationQueue.notificationQueueListObj
    : notificationListObjInitialvalue;
};
export const getSelectedNotifyJobId = (state: RootState): number => state.notificationQueue.selectedNotification.jobId;
export const getSelectedNotification = (state: RootState): INotificationsRaw | null =>
  state.notificationQueue.selectedNotification.notification;

export default getNotificationStatusSlice.reducer;
